import {
	type FrontendPlacementMessage,
	type AnyFrontendStage,
} from '@post-office/message-maker-browser/types';
import { type NextBestAction } from '@post-office/confluence-next-best-action';
import type {
	BackendPlacementMessage,
	CreateBackendPlacementMessageConfig,
} from '@post-office/message-maker-node/placements/types';
import {
	PlacementBackend,
	PlacementFrontend,
} from '@post-office/placements--growth/confluence-side-nav';

import { CreateWithRovo } from './components/create-with-rovo';
import { registeredContent } from './components/content';
import { assertMessageTemplateContext } from '../../../__generated__/validation/messageTemplateContext';
import { type MessageTemplateContext } from '../../../index';
import type {
	CreatePlacementMessageConfig,
	AnyPayload,
} from '@post-office/message-template-contracts/placement-v2';
import {
	type BackendSyncMessageStage,
	type AnyBackendMessageStage,
} from '@post-office/message-maker-node/placements/types';
import { RovoCcpId } from '@post-office/message-template-contracts/message-template-metadata';
import type { TransformMetadataResult } from '@post-office/shared-contracts/message-template-metadata';

const baseMessageBackend: (config: CreateBackendPlacementMessageConfig) => BackendPlacementMessage<{
	transformMetadata: BackendSyncMessageStage<AnyPayload, TransformMetadataResult>;
	hydrate?: AnyBackendMessageStage;
}> = PlacementBackend.createMessageBackend(() => ({
	transformMetadata: {
		validate: assertMessageTemplateContext,
		stage: () => ({
			rerankingMetadata: { productId: RovoCcpId },
		}),
	},
}));

export const MessageBackend: (
	config: CreateBackendPlacementMessageConfig,
) => BackendPlacementMessage<{
	transformMetadata: BackendSyncMessageStage<AnyPayload, TransformMetadataResult>;
	hydrate?: AnyBackendMessageStage;
}> = (config) =>
	baseMessageBackend({
		...config,
		content: registeredContent,
	});

type MessageFrontendProps = MessageTemplateContext & {
	handleOnDismissed: () => void;
	onSideNavClick: (nextBestActionType: NextBestAction) => string | void | null;
};

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: AnyFrontendStage;
}> = PlacementFrontend.createMessageFrontend({
	render: {
		validate: assertMessageTemplateContext,
		Component: ({
			nextBestActionType,
			handleOnDismissed,
			onSideNavClick,
		}: MessageFrontendProps) => {
			return (
				<CreateWithRovo
					action={nextBestActionType}
					onPrimaryActionClick={onSideNavClick}
					onDismissClick={handleOnDismissed}
				/>
			);
		},
	},
});
